import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/hooks/useSession";
import { Section } from "@/components/site/Section";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/begin-360-check")({
  head: () => ({
    meta: [
      { title: "Start your 360 check | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Enter your email and we send a link that opens your 360 Leader Behavior Check. No password to create.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Start your 360 check" },
      {
        property: "og:description",
        content: "We email you a link that opens your 360 Leader Behavior Check.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Begin360Page,
});

function Begin360Page() {
  const navigate = useNavigate();
  const { user } = useSession();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function signOut() {
    await supabase.auth.signOut().catch(() => undefined);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: `${window.location.origin}/my-review/`,
          data: { full_name: fullName },
        },
      });
      if (otpError) throw otpError;
      setSent(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "We couldn't send the link just now. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section tone="default">
      <div className="mx-auto max-w-lg">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
          360° Leader Behavior Check
        </p>
        <h1 className="mt-3 font-display text-3xl font-semibold leading-tight text-ink md:text-4xl">
          Start your check
        </h1>

        {user ? (
          <div className="mt-7 rounded-2xl border border-border/60 bg-background p-7">
            <h2 className="font-display text-xl font-semibold text-ink">You're already signed in</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Signed in as <span className="font-semibold text-ink">{user.email}</span>. Go to your
              checks, or sign out to use a different email.
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => navigate({ to: "/my-review/" })}
                className="inline-flex min-h-11 items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand"
              >
                Go to my checks
              </button>
              <button
                type="button"
                onClick={signOut}
                className="inline-flex min-h-11 items-center justify-center rounded-[6px] border border-primary px-6 py-3 text-sm font-semibold text-primary"
              >
                Sign out
              </button>
            </div>
          </div>
        ) : sent ? (
          <div className="mt-7 rounded-2xl border border-border/60 bg-background p-7">
            <h2 className="font-display text-xl font-semibold text-ink">Check your email</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              We sent a link to <span className="font-semibold text-ink">{email}</span>. Open it on
              this device and your check is ready to go. The link works once and expires in an hour.
            </p>
            <button
              type="button"
              onClick={() => setSent(false)}
              className="mt-5 text-sm font-semibold text-primary underline"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <>
            <p className="mt-3 max-w-md text-base leading-relaxed text-muted-foreground">
              Give us your email and we send a link that opens your check. No password to create.
            </p>
            <form
              onSubmit={handleSubmit}
              className="mt-7 space-y-5 rounded-2xl border border-border/60 bg-background p-7"
            >
              <div>
                <Label htmlFor="begin-name" className="text-sm font-semibold text-ink">
                  Your name
                </Label>
                <Input
                  id="begin-name"
                  autoComplete="name"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="begin-email" className="text-sm font-semibold text-ink">
                  Work email
                </Label>
                <Input
                  id="begin-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2"
                />
              </div>

              {error ? <p className="text-sm text-destructive">{error}</p> : null}

              <button
                type="submit"
                disabled={busy}
                className="inline-flex w-full items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              >
                {busy ? "Sending..." : "Email me my link"}
              </button>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Your raters never need an account. They answer through a private link you send them.
              </p>
            </form>
          </>
        )}

        <p className="mt-6 text-sm text-muted-foreground">
          Want to know how the check works first?{" "}
          <Link to="/360-leader-check/" className="font-semibold text-primary underline">
            Read the overview
          </Link>
          .
        </p>
      </div>
    </Section>
  );
}
